import type { PermissionKey } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, EmptyState, ErrorState, Icon, Input, Skeleton, Tooltip, cn, toast, useCopy } from "@sagot/ui";
import { useId, type ReactElement, type ReactNode } from "react";
import { usePermissions } from "../../../../session/permissions";
import { isPlanGateError, UpgradeCard } from "../../shared";

/** Whether the member may change this panel (permission + not a read-only support session). */
export function useCanManage(permission: PermissionKey): { allowed: boolean; reason: string | undefined; sessionReadOnly?: boolean } {
  const { t } = useTranslation();
  const permissions = usePermissions();
  if (permissions.readOnly) return { allowed: false, reason: t("console.readOnlyTooltip"), sessionReadOnly: true };
  if (!permissions.can(permission)) return { allowed: false, reason: t("console.noPermission") };
  return { allowed: true, reason: undefined };
}

/** Wraps a disabled control in a tooltip that explains why (hover + focus). */
export function Gate({ reason, children }: { reason: string | undefined; children: ReactElement }) {
  if (!reason) return children;
  return (
    <Tooltip content={reason}>
      <span className="inline-flex">
        {children}
      </span>
    </Tooltip>
  );
}

export function CardSkeleton({ rows = 3, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn("card", className)} aria-hidden="true">
      <div className="card-header">
        <Skeleton className="h-5 w-[180px]" />
      </div>
      <div className="card-body space-y-4">
        {Array.from({ length: rows }, (_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    </div>
  );
}

/** Load failure for a whole panel: 402 → upgrade card, otherwise friendly retry. */
export function PanelError({ error, onRetry, title }: { error: unknown; onRetry: () => void; title?: string }) {
  const { t } = useTranslation();
  if (isPlanGateError(error)) return <UpgradeCard title={t("settings.planGateTitle")} body={t("settings.planGateBody")} />;
  return (
    <div className="card">
      <ErrorState headingLevel={2} title={title ?? t("settings.loadError")} description={t("settings.loadErrorBody")} onRetry={onRetry} retryLabel={t("common.retry")} />
    </div>
  );
}

export function PanelForbidden() {
  const { t } = useTranslation();
  return (
    <div className="card" data-testid="settings-forbidden">
      <EmptyState icon="lock" headingLevel={2} title={t("settings.forbiddenTitle")} description={t("settings.forbiddenBody")} />
    </div>
  );
}

/** Secondary "Copy" button with a toast (mockup `data-copy`). */
export function CopyTextButton({ value, label, className }: { value: string; label?: string; className?: string }) {
  const { t } = useTranslation();
  const { copied, copy } = useCopy();
  return (
    <Button
      variant="secondary"
      className={className}
      onClick={() => {
        void copy(value).then((ok) => {
          if (ok) toast.success(t("common.copied"));
          else toast.error(t("settings.copyFailed"));
        });
      }}
    >
      <Icon name={copied ? "check" : "copy"} />
      {label ?? t("common.copy")}
    </Button>
  );
}

/** Show-once secret (API key, signing secret): warning + read-only monospace value + copy. */
export function SecretOnce({ value, warning, label }: { value: string; warning: ReactNode; label: string }) {
  const id = useId();
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-md border border-line bg-warning-soft p-3 text-xs text-fg">
        <Icon name="alert-triangle" className="mt-0.5 size-4 shrink-0 text-warning" />
        <span>{warning}</span>
      </div>
      <div>
        <label className="label" htmlFor={id}>
          {label}
        </label>
        <div className="flex gap-2">
          <Input id={id} readOnly value={value} className="font-mono text-xs" data-testid="secret-once" onFocus={(event) => { event.target.select(); }} />
          <CopyTextButton value={value} />
        </div>
      </div>
    </div>
  );
}

/** Card footer action bar (`border-t px-5 py-3`). */
export function CardActions({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap justify-end gap-2 border-t border-line px-5 py-3">{children}</div>;
}

/** Horizontal table scroller, keyboard-focusable (axe scrollable-region-focusable). */
export function TableScroll({ label, className, children }: { label: string; className?: string; children: ReactNode }) {
  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- focusable so keyboard users can scroll it (axe scrollable-region-focusable)
    <div className={cn("relative overflow-x-auto focus-visible:outline-2 focus-visible:outline-primary", className)} role="region" aria-label={label} tabIndex={0}>
      {children}
    </div>
  );
}

/** Dark code block (`.code`) that keyboard users can scroll, with a copy button. */
export function CodeSnippet({ code, label, className }: { code: string; label: string; className?: string }) {
  const { t } = useTranslation();
  return (
    <div className={cn("space-y-2", className)}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- scrollable code must be keyboard-focusable (axe scrollable-region-focusable) */}
      <pre className="code whitespace-pre" tabIndex={0} aria-label={label}>
        <code>{code}</code>
      </pre>
      <CopyTextButton value={code} label={t("common.copy")} />
    </div>
  );
}
